import { AfterViewInit, Component, ElementRef, Inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { combineLatest, debounceTime, Subject, take, takeUntil, timer } from 'rxjs';
import { TypeaheadMatch, TypeaheadModule } from 'ngx-bootstrap/typeahead';
import { NotificationsService } from 'angular2-notifications';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import Quill from 'quill';

import { TicketDetailsComponent } from 'src/app/ticket-details/ticket-details.component';
import { ReminderModalComponent } from '../../../../shared/modal-windows/reminder-modal/reminder-modal.component';
import { CommonDataService } from 'src/sb-tools/typescript/services/common-data.service';
import { HelperService } from 'src/sb-tools/typescript/services/helper.service';
import { SmilesService } from 'src/sb-tools/typescript/services/smiles.service';
import { UserService } from '@sb-tools/typescript/services/user.service';
import { ContactService } from '@sb-tools/typescript/services/contact.service';
import { ReplyTemplateService } from 'src/sb-modules/Frontend-Reply-Templates-Module/core/reply-template.service';
import { RightPanelService } from 'src/app/core/services/right-panel.service';
import { NoteService } from 'src/app/core/services/note.service';
import { ReplyDataService } from 'src/app/core/services/ticket/reply-data.service';
import { ForwardMessageService } from 'src/app/core/services/forward-message.service';
import { TicketService } from 'src/app/core/services/ticket/ticket.service';
import { ReplyService } from 'src/app/core/services/ticket/reply.service';
import { MessageDTO } from '@sb-tools/typescript/models/dto/message.dto';
import { AddressBookContactDTO } from '@sb-tools/typescript/models/dto/address-book-contact.dto';
import { BaseUserWithRole } from '@sb-tools/typescript/models/base-user-with-role.model';
import { ThreadElementDTO } from '@sb-tools/typescript/models/dto/thread-element.dto';
import { TicketReminderDTO } from '@sb-tools/typescript/models/dto/ticket-reminder.dto';
import { RightPanelType } from 'src/app/core/enums/right-panel-type.enum';
import { ForwardEntityTypeEnum } from 'src/app/core/enums/forward-entity-type.enum';
import { BACKSPACE, COMMA, ENTER, SEMICOLON, SPACE } from 'src/sb-tools/typescript/constants/keycodes';
import { DetailedTicketDTO } from '../../../../core/models/dto/detailed-ticket.dto';
import { WithLabelRecipients } from '../../../../core/models/with-label-recipients.model';
import { HideContentPartResponseDTO } from '../../../../core/models/dto/hide-content-part-response.dto';
import { EmailRecipients } from '../../../../core/models/email-recipients.model';
import { getUrlWithQueryParams } from '../../../../core/utils/get-url-with-query-params.util';
import { NoteLinkLineComponent } from '../../note-link-line/note-link-line.component';
import { ReplyReminderAlertComponent } from '../../../../shared/components/reply-reminder-alert/reply-reminder-alert.component';
import { DatePipe, NgClass } from '@angular/common';
import { UserAvatarComponent } from '../../../../../sb-modules/Frontend-User-Avatar-Module/user-avatar.component';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { InlineSVGComponent } from '../../../../../sb-modules/Frontend-Inline-SVG-Module/inline-svg.component';
import { ClipboardModule } from 'ngx-clipboard';
import { ReminderMenuComponent } from '../../../../shared/components/reminder-menu/reminder-menu.component';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { InlineImagePreviewDirective } from '../../../../../sb-modules/Frontend-Document-Viewer-Module/directives/inline-image-preview.directive';
import { InlineImageLimitWidthDirective } from '../../../../shared/directives/inline-image-limit-width.directive';
import { FileViewComponent } from '../../../../../sb-modules/Frontend-Document-Viewer-Module/components/file-view/file-view.component';
import { FilePreviewComponent } from '../../../../../sb-modules/Frontend-Document-Viewer-Module/components/file-preview/file-preview.component';
import { TagsInputComponent } from '../../../../../sb-modules/Frontend-form-field-module/tags-input/tags-input.component';
import { FormsModule } from '@angular/forms';
import { QuillReplyTemplatesComponent } from '../../../../../sb-modules/Frontend-Reply-Templates-Module/quill-reply-templates/quill-reply-templates.component';
import { WebReplyQuillEditorComponent } from '../../../../shared/components/web-reply-quill-editor/web-reply-quill-editor.component';
import { QuillEmojiPickerComponent } from '../../../../../sb-tools/typescript/components/quill-emoji-picker/quill-emoji-picker.component';
import { TagsComponent } from '../../../tags/tags.component';
import { TicketEmailTimePipe } from '../../../../../sb-tools/typescript/pipes/ticket/ticket-email-time.pipe';
import { ReplaceEmojisPipe } from '../../../../../sb-tools/typescript/pipes/replace-emoji/replace-emojis.pipe';
import { IsInvalidMessagePipe } from '../../../../../sb-tools/typescript/pipes/ticket/is-invalid-message.pipe';
import { PrepareMessagePipe } from '../../../../../sb-tools/typescript/pipes/ticket/prepare-message.pipe';
import { TruncatePipe } from '../../../../../sb-tools/typescript/pipes/truncate.pipe';
import { JoinPipe } from '../../../../../sb-tools/typescript/pipes/join.pipe';
import { ArrayToStringPipe } from '../../../../shared/pipes/array-to-string.pipe';
import { HideContentPipe } from '../../../../shared/pipes/hide-content.pipe';
import { isInboxElementVisible } from '../../../../core/utils/is-inbox-element-visible.util';

@Component({
    selector: 'app-ticket-web-reply',
    templateUrl: './ticket-web-reply.component.html',
    styleUrls: ['./ticket-web-reply.component.scss'],
    imports: [
        NoteLinkLineComponent,
        ReplyReminderAlertComponent,
        NgClass,
        UserAvatarComponent,
        BsDropdownModule,
        TooltipModule,
        InlineSVGComponent,
        TranslateModule,
        ClipboardModule,
        ReminderMenuComponent,
        PopoverModule,
        InlineImagePreviewDirective,
        InlineImageLimitWidthDirective,
        FileViewComponent,
        FilePreviewComponent,
        TagsInputComponent,
        FormsModule,
        TypeaheadModule,
        QuillReplyTemplatesComponent,
        WebReplyQuillEditorComponent,
        QuillEmojiPickerComponent,
        TagsComponent,
        DatePipe,
        TicketEmailTimePipe,
        ReplaceEmojisPipe,
        IsInvalidMessagePipe,
        PrepareMessagePipe,
        TruncatePipe,
        JoinPipe,
        ArrayToStringPipe,
        HideContentPipe,
    ],
})
export class TicketWebReplyComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('replyMenuBtn') replyMenuBtn: ElementRef;

    @Input() quillConfig;
    @Input() reply: ThreadElementDTO;

    forwardEntityTypeEnum = ForwardEntityTypeEnum;
    isEmailValid = HelperService.isEmailValid;
    isLoadingOriginalMessage: boolean;
    isReplyMenuDropUp: boolean;
    isReminderMenuDropUp: boolean;
    isShowBccFieldForDraft = false;
    isShowCcFieldForDraft = false;
    isShowFromField: boolean;
    // todo: remove when postnord start using chat view
    isToAndCcHidden: boolean;
    originalMessage: MessageDTO;
    quillEditor: Quill;
    recipientsUpdate = new Subject<WithLabelRecipients>();
    replyUrl: string;
    rightPanelTypeEnum = RightPanelType;
    selectedBccUsers: BaseUserWithRole[] = [];
    selectedCcUsers: BaseUserWithRole[] = [];
    selectedOutboundEmailAddress: string;
    selectedOutboundEmailAddresses: string[] = [];
    selectedToUsers: BaseUserWithRole[] = [];
    separatorKeys = [SPACE, ENTER, COMMA, SEMICOLON];
    ticket: DetailedTicketDTO;
    users: (BaseUserWithRole | AddressBookContactDTO)[] = [];
    readonly isInboxElementVisible = isInboxElementVisible;

    private unsubscriber = new Subject<void>();

    constructor(
        public rightPanelService: RightPanelService,
        public commonDataService: CommonDataService,
        public noteService: NoteService,
        @Inject(TicketDetailsComponent) public parent: TicketDetailsComponent,
        public replyTemplateService: ReplyTemplateService,
        public forwardMessageService: ForwardMessageService,
        private contactService: ContactService,
        private replyDataService: ReplyDataService,
        private smilesService: SmilesService,
        private ticketService: TicketService,
        private translateService: TranslateService,
        private userService: UserService,
        private notificationsService: NotificationsService,
        private replyService: ReplyService,
        private bsModalService: BsModalService,
    ) {
        this.ticket = parent.ticket;

        this.isToAndCcHidden = ['postnordsverige', 'postnordsetest', 'nataliatestdev', 'nataliafirsttest'].includes(
            commonDataService.community.toLowerCase(),
        );
    }

    ngOnInit() {
        this.reply.isLogged = false;
        combineLatest([this.contactService.getAllFromCache(), this.userService.getAll()])
            .pipe(takeUntil(this.unsubscriber), take(1))
            .subscribe(([contacts, users]) => {
                contacts.forEach((contact) => {
                    contact.label = contact.name + ' ' + contact.email;
                    contact.typeName = this.translateService.instant('Contacts');
                    contact.isContact = true;
                });
                users.forEach((user) => {
                    user.label = user.name ? user.name + ' ' + user.email : user.email;
                    user.typeName = this.translateService.instant('Teammates');
                });

                this.users = [...contacts, ...users];

                if (this.users.length && this.reply.editingReply) {
                    if (this.reply.emailInfo.cc.length) {
                        this.isShowCcFieldForDraft = true;
                        this.selectedCcUsers = this.getSelectedUsersList(this.reply.emailInfo.cc);
                    }

                    if (this.reply.emailInfo.bcc.length) {
                        this.isShowBccFieldForDraft = true;
                        this.selectedBccUsers = this.getSelectedUsersList(this.reply.emailInfo.bcc);
                    }

                    if (this.reply.emailInfo.to.length) {
                        this.selectedToUsers = this.getSelectedUsersList(this.reply.emailInfo.to);
                    }
                }
            });

        this.recipientsUpdate
            .pipe(debounceTime(700), takeUntil(this.unsubscriber))
            .subscribe((withLabelRecipients: WithLabelRecipients) => {
                const toUsers = withLabelRecipients.to.map((u) => u.email);
                const ccUsers = withLabelRecipients.cc.map((u) => u.email);
                const bccUsers = withLabelRecipients.bcc.map((u) => u.email);
                this.validateReplyRecipients({ to: toUsers, cc: ccUsers, bcc: bccUsers });
            });

        if (this.parent.ticket.outboundEmailAddress) {
            this.isShowFromField = true;
            this.selectedOutboundEmailAddresses[0] = this.parent.ticket.outboundEmailAddress;
        }

        if (this.reply.id) {
            this.replyUrl = getUrlWithQueryParams({
                additionalParams: { ticketId: this.ticket.id, replyId: this.reply.id },
                withCurrentParams: false,
            });
        }
    }

    ngAfterViewInit() {
        this.replyMenuBtn.nativeElement.addEventListener('mouseenter', () => {
            this.initDropdownsPositioning();
        });

        timer(50).subscribe(() => {
            this.initDropdownsPositioning();
        });
    }

    addOutboundEmailAddress(event: TypeaheadMatch, callback?: () => void) {
        this.selectedOutboundEmailAddresses[0] = event.item;

        if (callback) {
            callback();
        }

        this.selectedOutboundEmailAddress = '';
    }

    checkAndAddNewEmail(callback?: () => void): void {
        if (HelperService.isEmailValid(this.selectedOutboundEmailAddress)) {
            this.selectedOutboundEmailAddress = this.selectedOutboundEmailAddress.toLowerCase();

            this.addOutboundEmailAddress(
                {
                    item: this.selectedOutboundEmailAddress,
                } as TypeaheadMatch,
                callback,
            );
        }
    }

    deleteHiddenContent(): void {
        this.ticketService
            .deleteHiddenContent(this.ticket.id, this.reply.id)
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((hideContent: HideContentPartResponseDTO) => {
                this.reply.hasContentPartHiddenForClient = hideContent.hasContentPartHiddenForClient;
                this.reply.isContentHiddenForClient = hideContent.isContentHiddenForClient;
                this.reply.message = hideContent.message;

                this.notificationsService.success(
                    this.translateService.instant('Success!'),
                    this.translateService.instant('The content was successfully showed.'),
                );
            });
    }

    initDropdownsPositioning(): void {
        const [isReplyMenuDropUp, isReminderMenuDropUp] = this.replyService.getReplyMenusDropUp(this.replyMenuBtn);
        this.isReplyMenuDropUp = isReplyMenuDropUp;
        this.isReminderMenuDropUp = isReminderMenuDropUp;
    }

    keyDownHandler(event): boolean {
        if (this.separatorKeys.includes(event.keyCode)) {
            const cleanSearchTextCallback = () => {
                event.target.value = '';
            };
            this.checkAndAddNewEmail(cleanSearchTextCallback);
            return false;
        }

        if (event && event.keyCode === BACKSPACE && !event.target.value) {
            this.selectedOutboundEmailAddresses = [];
        }
    }

    onRemindClick(minutes: number): void {
        const dateTime = new Date(new Date().getTime() + 60000 * minutes);

        const reminder: TicketReminderDTO = {
            time: dateTime,
            replyId: this.reply.id,
        };

        this.ticketService
            .addReminder(this.ticket.id, reminder)
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((addedReminder: TicketReminderDTO) => {
                this.reply.reminder = addedReminder;
                this.parent.ticketUpdateStamp = new Date().getTime();
                this.notificationsService.success(
                    this.translateService.instant('Success!'),
                    this.translateService.instant('Reminder was successfully added.'),
                );
            });
    }

    onRemoveReminderClick(): void {
        this.ticketService
            .deleteReminder(this.ticket.id, this.reply.id)
            .pipe(takeUntil(this.unsubscriber))
            .subscribe(() => {
                this.reply.reminder = null;
                this.parent.ticketUpdateStamp = new Date().getTime();
                this.notificationsService.success(
                    this.translateService.instant('Success!'),
                    this.translateService.instant('Reminder was successfully removed.'),
                );
            });
    }

    onReplyChanged(reply) {
        reply.text = HelperService.strip(this.smilesService.replaceImagesToCodes(reply.html), false);
    }

    openHideContentPanel(): void {
        const selectedText = window.getSelection().toString().trim();
        this.rightPanelService.open(RightPanelType.hideTicketContent, {
            ticket: this.ticket,
            replyId: this.reply.id,
            hidingText: selectedText,
        });
    }

    openReminderModal(): void {
        const initialState = {
            ticketId: this.ticket.id,
            replyId: this.reply.id,
            hasReopenOption: false,
            confirmCallback: (reminder: TicketReminderDTO) => {
                this.reply.reminder = reminder;
                this.parent.ticketUpdateStamp = new Date().getTime();
            },
        };

        this.bsModalService.show(ReminderModalComponent, {
            initialState,
            ignoreBackdropClick: true,
        });
    }

    removeOutboundEmailAddress(): void {
        this.selectedOutboundEmailAddresses = [];
    }

    showAndFocusCcFieldForDraft(replyId) {
        this.isShowCcFieldForDraft = true;
        setTimeout(() => {
            (document.querySelector('[data-reply-id="' + replyId + '"]') as HTMLElement).focus();
        }, 100);
    }

    showAndFocusBccFieldForDraft(replyId) {
        this.isShowBccFieldForDraft = true;
        setTimeout(() => {
            (document.querySelector('[data-reply-id="' + replyId + '"]') as HTMLElement).focus();
        }, 100);
    }

    showFromField(): void {
        this.isShowFromField = true;
    }

    showOriginalMessage(ticketId: number, replyId: number) {
        const newWindow = window.open();
        if (!this.originalMessage && !this.isLoadingOriginalMessage) {
            this.isLoadingOriginalMessage = true;
            this.replyDataService
                .getOriginalMessage(ticketId, replyId)
                .pipe(takeUntil(this.unsubscriber))
                .subscribe((result: MessageDTO) => {
                    this.originalMessage = result;
                    this.isLoadingOriginalMessage = false;
                    newWindow.document.write(result.html || result.text);
                });
            return;
        }

        newWindow.document.write(this.originalMessage.html || this.originalMessage.text);
    }

    validateReplyRecipients(emailRecipients: EmailRecipients) {
        this.reply.isRecipientChangeConfirmed = null;
        this.replyDataService
            .validateReplyRecipients(this.ticket.id, emailRecipients)
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((result) => {
                this.reply.isRecipientChangeConfirmed = result;
            });
    }

    private getSelectedUsersList(emailsList): BaseUserWithRole[] {
        return emailsList.map((email: string) => {
            const existedUser = this.users.find((user) => {
                return user.email.toLowerCase() === email.toLowerCase();
            });

            if (existedUser) {
                return existedUser;
            } else {
                return {
                    email,
                    label: email,
                } as BaseUserWithRole;
            }
        });
    }

    ngOnDestroy() {
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }
}
